import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertCircle, CheckCircle2, XCircle, Clock, Gem, Info } from 'lucide-react'
import useWithdrawStore from '../store/useWithdrawStore'
import useAppStore from '../../../store/useAppStore'
import toast from 'react-hot-toast'

/**
 * SteamTopup — компонент вывода кристаллов на Steam-аккаунт
 * Содержит форму ввода, live-расчёт суммы, блок активной заявки и историю
 */
const SteamTopup = () => {
  const { t } = useTranslation()
  const { balance, loadProfile } = useAppStore()
  const { info, history, isLoading, isSubmitting, loadData, submitSteamTopup, stopPolling } = useWithdrawStore()

  const [steamLogin, setSteamLogin] = useState('')
  const [crystals, setCrystals] = useState('')
  const [validationError, setValidationError] = useState(null)

  // Загрузка данных при монтировании
  useEffect(() => {
    loadData()
    return () => stopPolling()
  }, [loadData, stopPolling])

  // Подставляем последний Steam-логин при загрузке
  useEffect(() => {
    if (info?.last_steam_login) {
      setSteamLogin(info.last_steam_login)
    }
  }, [info?.last_steam_login])

  // Live-расчёт суммы в долларах
  // Формула из документации: Math.floor(crystals * 100 / crystals_per_dollar_net) / 100
  const usdAmount = useMemo(() => {
    const crystalsNum = parseInt(crystals, 10)
    if (!crystalsNum || !info?.crystals_per_dollar_net) return 0
    const usdCents = Math.floor(crystalsNum * 100 / info.crystals_per_dollar_net)
    return usdCents / 100
  }, [crystals, info?.crystals_per_dollar_net])

  // Валидация формы
  const validate = () => {
    // Проверка Steam-логина: A-Z a-z 0-9 _ -, 3-64 символа
    const loginRegex = /^[A-Za-z0-9_-]{3,64}$/
    if (!steamLogin) {
      setValidationError(t('withdraw.steam.loginRequired', 'Введите логин Steam'))
      return false
    }
    if (!loginRegex.test(steamLogin)) {
      setValidationError(t('withdraw.steam.loginInvalid', 'Логин Steam: 3–64 символа, только буквы, цифры, _ и -'))
      return false
    }

    const crystalsNum = parseInt(crystals, 10)
    if (!crystalsNum || crystalsNum < (info?.min_crystals || 0)) {
      setValidationError(t('withdraw.steam.minAmount', 'Минимальная сумма') + ': ' + (info?.min_crystals?.toLocaleString('en-US') || ''))
      return false
    }
    if (crystalsNum > (info?.max_crystals || 0)) {
      setValidationError(t('withdraw.steam.maxAmount', 'Максимальная сумма') + ': ' + (info?.max_crystals?.toLocaleString('en-US') || ''))
      return false
    }
    if (crystalsNum > balance) {
      setValidationError(t('withdraw.steam.insufficientBalance', 'Недостаточно кристаллов'))
      return false
    }

    setValidationError(null)
    return true
  }

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e?.preventDefault()

    if (!validate()) return

    try {
      const result = await submitSteamTopup(steamLogin, parseInt(crystals, 10))

      // Если сумма из ответа отличается от показанной — сообщаем
      if (result.usd && result.usd !== usdAmount) {
        toast.success(t('withdraw.steam.successWithAdjustedAmount', {
          defaultValue: 'Заявка создана! Сумма: ${{usd}}',
          usd: result.usd,
        }))
      } else {
        toast.success(t('withdraw.steam.success', 'Заявка на вывод создана!'))
      }

      // Обновляем баланс
      await loadProfile()

      // Очищаем поле суммы
      setCrystals('')
    } catch (error) {
      // Обработка кодов ошибок из документации
      const errorMsg = error.message || ''
      const errorMap = {
        'disabled': t('withdraw.steam.errors.disabled', 'Вывод в Steam временно недоступен'),
        'withdraw banned': t('withdraw.steam.errors.withdrawBanned', 'Вывод заблокирован, обратитесь в поддержку'),
        'deposit required': t('withdraw.steam.errors.depositRequired', 'Вывод доступен после первого пополнения баланса'),
        'active request': t('withdraw.steam.errors.activeRequest', 'Дождитесь завершения текущей заявки'),
        'invalid login': t('withdraw.steam.errors.invalidLogin', 'Проверьте логин Steam'),
        'error sum': t('withdraw.steam.errors.errorSum', 'Сумма вне лимитов'),
        'insufficient balance': t('withdraw.steam.errors.insufficientBalance', 'Недостаточно кристаллов'),
        'error backend': t('withdraw.steam.errors.errorBackend', 'Техническая ошибка, попробуйте позже'),
      }

      const matchedMsg = Object.entries(errorMap).find(([key]) => errorMsg.includes(key))
      toast.error(matchedMsg ? matchedMsg[1] : t('withdraw.steam.errors.errorBackend', 'Техническая ошибка, попробуйте позже'))
    }
  }

  // Проверка блокировки формы
  const isFormDisabled = !info?.can_withdraw || info?.has_active || isSubmitting
  const isBlockedReason = !info?.can_withdraw
    ? t('withdraw.steam.blockedCannotWithdraw', 'Вывод недоступен. Обратитесь в поддержку или пополните баланс впервые.')
    : info?.has_active
      ? t('withdraw.steam.blockedHasActive', 'У вас уже есть активная заявка. Дождитесь её завершения.')
      : null

  // Конфигурация статусов для истории
  const getStatusConfig = (status) => {
    switch (status) {
      case 'new':
      case 'pending':
        return {
          icon: Loader2,
          iconClass: 'text-blue-400 animate-spin',
          textClass: 'text-blue-400',
          label: t('withdraw.steam.status.processing', 'В обработке'),
          containerClass: 'border-blue-500/20 bg-blue-500/5',
        }
      case 'waiting':
        return {
          icon: Clock,
          iconClass: 'text-blue-400',
          textClass: 'text-blue-400',
          label: t('withdraw.steam.status.waiting', 'Отправлено в Steam'),
          containerClass: 'border-blue-500/20 bg-blue-500/5',
        }
      case 'success':
        return {
          icon: CheckCircle2,
          iconClass: 'text-accent-primary',
          textClass: 'text-accent-primary',
          label: t('withdraw.steam.status.success', 'Зачислено'),
          containerClass: 'border-accent-primary/20 bg-accent-primary/5',
        }
      case 'cancelled':
        return {
          icon: XCircle,
          iconClass: 'text-red-400',
          textClass: 'text-red-400',
          label: t('withdraw.steam.status.cancelled', 'Отменено, кристаллы возвращены'),
          containerClass: 'border-red-500/20 bg-red-500/5',
        }
      case 'stuck':
        return {
          icon: AlertCircle,
          iconClass: 'text-yellow-400',
          textClass: 'text-yellow-400',
          label: t('withdraw.steam.status.stuck', 'На проверке — обратитесь в поддержку'),
          containerClass: 'border-yellow-500/20 bg-yellow-500/5',
        }
      default:
        return {
          icon: Clock,
          iconClass: 'text-text-secondary',
          textClass: 'text-text-secondary',
          label: status,
          containerClass: 'border-white/10 bg-surface',
        }
    }
  }

  // Активная заявка из истории (первая с не-финальным статусом)
  const activeRequest = history.find((h) => ['new', 'pending', 'waiting'].includes(h.status))

  if (isLoading && !info) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={32} className="text-accent-primary animate-spin" />
      </div>
    )
  }

  if (!info?.enabled) {
    return (
      <div className="text-center py-16">
        <AlertCircle size={48} className="mx-auto text-text-secondary mb-4 opacity-50" />
        <p className="text-text-secondary">{t('withdraw.steam.disabled', 'Вывод в Steam временно недоступен')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Блок активной заявки */}
      {activeRequest && (
        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 size={20} className="text-blue-400 animate-spin" />
            <span className="font-semibold text-blue-400">
              {t('withdraw.steam.activeRequest', 'Активная заявка')} #{activeRequest.id}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">
              <bdi dir="ltr">{activeRequest.steam_login}</bdi> · <bdi dir="ltr">{activeRequest.crystals.toLocaleString('en-US')}</bdi> <Gem size={12} className="inline text-accent-primary" />
            </span>
            <bdi dir="ltr" className="font-bold text-white">${activeRequest.usd}</bdi>
          </div>
          <p className="text-xs text-text-secondary mt-2">
            {t('withdraw.steam.activeRequestDesc', 'Заявка обрабатывается. Уведомление о завершении придёт в Telegram.')}
          </p>
        </div>
      )}

      {/* Форма вывода */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Steam-логин */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('withdraw.steam.steamLogin', 'Логин Steam')}
          </label>
          <div className="relative">
            <img src="/images/payment/steam.png" alt="Steam" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
            <input
              type="text"
              dir="ltr"
              value={steamLogin}
              onChange={(e) => setSteamLogin(e.target.value)}
              disabled={isFormDisabled}
              placeholder="my_steam_login"
              maxLength={64}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          <p className="mt-2 text-xs text-text-secondary flex items-center gap-1">
            <Info size={12} />
            {t('withdraw.steam.loginHint')}
          </p>
        </div>

        {/* Сумма в кристаллах */}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-2">
            {t('withdraw.steam.amount', 'Сумма (кристаллы)')}
          </label>
          <div className="relative">
            <Gem size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-accent-primary" />
            <input
              type="text"
              dir="ltr"
              inputMode="numeric"
              value={crystals ? Number(crystals).toLocaleString('en-US') : ''}
              onChange={(e) => setCrystals(e.target.value.replace(/\D/g, ''))}
              disabled={isFormDisabled}
              placeholder={info?.min_crystals?.toLocaleString('en-US') || '0'}
              className="w-full bg-dark-primary border border-white border-opacity-10 rounded-lg pl-10 pr-4 py-3 text-white focus:border-accent-primary focus:outline-none disabled:opacity-50"
            />
          </div>
          <div className="mt-2 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => info?.min_crystals && setCrystals(String(info.min_crystals))}
              disabled={isFormDisabled}
              className="text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('withdraw.steam.min', 'Мин.')}: {info?.min_crystals?.toLocaleString('en-US') || '—'}
            </button>
            <button
              type="button"
              onClick={() => info?.max_crystals && setCrystals(String(Math.min(info.max_crystals, balance)))}
              disabled={isFormDisabled}
              className="text-text-secondary hover:text-accent-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('withdraw.steam.max', 'Макс.')}: {info?.max_crystals?.toLocaleString('en-US') || '—'}
            </button>
          </div>
        </div>

        {/* Расчёт суммы в долларах */}
        {usdAmount > 0 && (
          <div className="rounded-lg bg-accent-primary/10 border border-accent-primary/20 p-4 text-center">
            <p className="text-sm text-text-secondary mb-1">{t('withdraw.steam.youWillReceive', 'Вы получите')}</p>
            <bdi dir="ltr" className="text-3xl font-bold text-accent-primary">${usdAmount.toFixed(2)}</bdi>
          </div>
        )}

        {/* Ошибка валидации */}
        {validationError && (
          <div className="flex items-center gap-2 text-sm text-red-400">
            <AlertCircle size={16} />
            <span>{validationError}</span>
          </div>
        )}

        {/* Причина блокировки формы */}
        {isBlockedReason && (
          <div className="flex items-center gap-2 text-sm text-yellow-400 rounded-lg bg-yellow-500/10 border border-yellow-500/20 p-3">
            <AlertCircle size={16} />
            <span>{isBlockedReason}</span>
          </div>
        )}

        {/* Кнопка отправки */}
        <button
          type="submit"
          disabled={isFormDisabled}
          className="w-full py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              {t('withdraw.steam.submitting', 'Создание заявки...')}
            </>
          ) : (
            t('withdraw.steam.submit', 'Вывести в Steam')
          )}
        </button>
      </form>

      {/* История заявок */}
      {history.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-white mb-3">
            {t('withdraw.steam.historyTitle', 'История выводов')}
          </h3>
          <div className="space-y-2">
            {history.map((item) => {
              const statusConfig = getStatusConfig(item.status)
              const StatusIcon = statusConfig.icon

              return (
                <div
                  key={item.id}
                  className={`rounded-lg border p-3 ${statusConfig.containerClass}`}
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon size={18} className={statusConfig.iconClass} />
                    <div>
                      <div className="flex items-center gap-2">
                        <bdi dir="ltr" className="text-sm font-medium text-white">#{item.id}</bdi>
                        <bdi dir="ltr" className="text-xs text-text-secondary">{item.steam_login}</bdi>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-text-secondary">
                          <bdi dir="ltr">{item.crystals.toLocaleString('en-US')}</bdi> <Gem size={12} className="inline text-accent-primary" /> → <bdi dir="ltr">${item.usd}</bdi>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white border-opacity-5">
                    <div className={`text-xs font-medium ${statusConfig.textClass}`}>
                      {statusConfig.label}
                    </div>
                    {item.error && item.status === 'cancelled' && (
                      <div className="text-xs text-red-400 mt-1 break-words">
                        {item.error}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default SteamTopup
